import { SearchXIcon } from "lucide-react"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { Table, TableBody } from "@/components/ui/table"
import { useAuth } from "@/contexts/auth"
import useSmartRoutingConfigurations from "@/hooks/use-smart-routing-configurations"
import useWhiteLabels from "@/hooks/use-whitelabels"
import type { SmartRoutingConfiguration } from "@/types/smart-routing"

import { AppSkeletonTable } from "../commons/app-skeleton-table"
import {
  TableContent,
  TableHeaderContent,
} from "./table-components"

const COLUMN_COUNT = 10

export const SmartRoutingTable = () => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { selectedWhiteLabel } = useAuth()
  const { data: whitelabelsData } = useWhiteLabels()

  const statusFilter = searchParams.get("status") ?? "ALL"

  const whitelabelUuids = useMemo(() => {
    if (selectedWhiteLabel?.uuid)
      return [selectedWhiteLabel.uuid]
    return (whitelabelsData ?? [])
      .map((wl) => wl.uuid)
      .filter(Boolean)
  }, [selectedWhiteLabel, whitelabelsData])

  const { data, isFetching } =
    useSmartRoutingConfigurations(whitelabelUuids)
  const allConfigurations = data?.configurations ?? []

  const visibleConfigurations = allConfigurations.filter(
    (config: SmartRoutingConfiguration) => {
      const matchesStatus =
        statusFilter === "ALL" ||
        config.statusDetail.status === statusFilter
      return matchesStatus
    }
  )

  if (isFetching)
    return <AppSkeletonTable columns={COLUMN_COUNT} />

  if (visibleConfigurations.length === 0) {
    return (
      <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-4 text-muted-foreground">
        <SearchXIcon size={48} strokeWidth={1.5} />
        <div className="text-center">
          <p className="font-medium">
            {t("pages.smartRouting.noConfigurationsFound")}
          </p>
          <p className="text-sm">
            {t("pages.smartRouting.tryDifferentFilters")}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-2 overflow-hidden rounded-md border border-border pb-2">
      <Table className="relative h-10 w-full overflow-clip rounded-md border-border">
        <TableHeaderContent />
        <TableBody>
          <TableContent data={visibleConfigurations} />
        </TableBody>
      </Table>
    </div>
  )
}
